import { useEffect } from "react";

const SITE_URL = "https://naughty-galois-6.emergent.host";
const DEFAULT_OG_IMAGE = `${SITE_URL}/hero/hero.jpg`;

/**
 * useSEO — page-level title/description/canonical + OG/Twitter manager.
 * No external dependency. Safe to call once per page.
 */
export default function useSEO({ title, description, ogImage, path } = {}) {
  useEffect(() => {
    if (title) document.title = title;

    const ensureMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      return el;
    };

    const ensureLink = (rel) => {
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      return el;
    };

    if (description) {
      ensureMeta('meta[name="description"]', { name: "description" })
        .setAttribute("content", description);
      ensureMeta('meta[property="og:description"]', { property: "og:description" })
        .setAttribute("content", description);
      ensureMeta('meta[name="twitter:description"]', { name: "twitter:description" })
        .setAttribute("content", description);
    }
    if (title) {
      ensureMeta('meta[property="og:title"]', { property: "og:title" })
        .setAttribute("content", title);
      ensureMeta('meta[name="twitter:title"]', { name: "twitter:title" })
        .setAttribute("content", title);
    }
    const finalOg = ogImage
      ? (ogImage.startsWith("http") ? ogImage : `${SITE_URL}${ogImage}`)
      : DEFAULT_OG_IMAGE;
    ensureMeta('meta[property="og:image"]', { property: "og:image" })
      .setAttribute("content", finalOg);
    ensureMeta('meta[name="twitter:image"]', { name: "twitter:image" })
      .setAttribute("content", finalOg);

    // Canonical
    const canonicalPath = path || (typeof window !== "undefined" ? window.location.pathname : "/");
    ensureLink("canonical").setAttribute("href", `${SITE_URL}${canonicalPath}`);
    ensureMeta('meta[property="og:url"]', { property: "og:url" })
      .setAttribute("content", `${SITE_URL}${canonicalPath}`);
  }, [title, description, ogImage, path]);
}
